import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { useThemeMode } from "@/hooks/useThemeMode";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, Settings as SettingsIcon, Shield, ShieldCheck, Sliders, UserCog, Activity, User, CreditCard, Globe, FileDown, Bell, Moon, CalendarClock, Zap } from "lucide-react";
import { useCanManageOrg } from "@/lib/auth/roles";
import { SubscriptionBanner } from "@/components/subscription/SubscriptionBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useSubscription } from "@/hooks/useSubscription";
import { getTierRank } from "@/lib/subscriptionTiers";
import { ROUTES } from '@/lib/routes';


export default function Settings() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canManageOrg = useCanManageOrg();
  const { isBetaOrg, effectiveTier } = useSubscription();
  const tierRank = getTierRank(effectiveTier);
  const { isDark, toggle: setDark } = useThemeMode();

  // Handle subscription return redirects
  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      toast.success(translate('settings_toast_subscription_success'), {
        description: translate('settings_toast_subscription_success_desc'),
      });
      window.history.replaceState({}, '', ROUTES.SETTINGS);
    } else if (subscriptionStatus === 'cancelled') {
      toast.info(translate('settings_toast_checkout_cancelled'), {
        description: translate('settings_toast_checkout_cancelled_desc'),
      });
      window.history.replaceState({}, '', ROUTES.SETTINGS);
    }
  }, [searchParams, translate]);

  const settingsSections = [
    {
      titleKey: "settings_section_account",
      descriptionKey: "settings_section_account_desc",
      icon: User,
      href: ROUTES.SETTINGS_ACCOUNT,
      requiresOrgAdmin: false,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_subscription",
      descriptionKey: "settings_section_subscription_desc",
      icon: CreditCard,
      href: ROUTES.SETTINGS_BILLING,
      requiresOrgAdmin: true,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_organization",
      descriptionKey: "settings_section_organization_desc",
      icon: Building2,
      href: ROUTES.SETTINGS_ORGANIZATION,
      requiresOrgAdmin: false,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_team",
      descriptionKey: "settings_section_team_desc",
      icon: UserCog,
      href: ROUTES.SETTINGS_TEAM,
      requiresOrgAdmin: true,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_role_permissions",
      descriptionKey: "settings_section_role_permissions_desc",
      icon: ShieldCheck,
      href: ROUTES.SETTINGS_ROLE_PERMISSIONS,
      requiresOrgAdmin: true,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_squads",
      descriptionKey: "settings_section_squads_desc",
      icon: Users,
      href: ROUTES.SETTINGS_SQUADS,
      requiresOrgAdmin: false,
      hideForBeta: false,
      minTierRank: 1, // Foundation+
    },
    {
      titleKey: "settings_section_security",
      descriptionKey: "settings_section_security_desc",
      icon: Shield,
      href: ROUTES.SETTINGS_SECURITY_COMPLIANCE,
      requiresOrgAdmin: false,
      hideForBeta: false,
      minTierRank: 1, // Foundation+
    },
    {
      titleKey: "settings_section_data_export",
      descriptionKey: "settings_section_data_export_desc",
      icon: FileDown,
      href: ROUTES.SETTINGS_DATA_EXPORT,
      requiresOrgAdmin: true,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_notifications",
      descriptionKey: "settings_section_notifications_desc",
      icon: Bell,
      href: ROUTES.SETTINGS_NOTIFICATIONS,
      requiresOrgAdmin: false,
      hideForBeta: false,
      minTierRank: 0,
    },
    {
      titleKey: "settings_section_kiosk",
      descriptionKey: "settings_section_kiosk_desc",
      icon: Activity,
      href: ROUTES.SETTINGS_KIOSK,
      requiresOrgAdmin: false,
      hideForBeta: false,
      minTierRank: 0,
    },
  ].filter(section => {
    if (section.requiresOrgAdmin && !canManageOrg) return false;
    if (section.hideForBeta && isBetaOrg) return false;
    if (tierRank < section.minTierRank) return false;
    return true;
  });

  return (
    <div className="container mx-auto p-6 max-w-5xl space-y-6">
      <PageHeader
        title={translate("settings_title")}
        description={translate("settings_description")}
        icon={<SettingsIcon className="h-8 w-8 text-primary" />}
      />

      <SubscriptionBanner />

      {/* Language & Appearance */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Globe className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-lg">{translate("settings_language_title")}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label htmlFor="language-select">{translate("settings_language_label")}</Label>
              <LanguageSwitcher />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Moon className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-lg">Appearance</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Label htmlFor="dark-mode-switch">Dark mode</Label>
              <Switch
                id="dark-mode-switch"
                checked={isDark}
                onCheckedChange={setDark}
              />
            </div>
          </CardContent>
        </Card>
      </div>


      <div className="grid gap-4 md:grid-cols-2">
        {settingsSections.map((section) => {
          const Icon = section.icon;
          return (
            <Card
              key={section.href}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => navigate(section.href)}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{translate(section.titleKey)}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription>{translate(section.descriptionKey)}</CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
